// src/utils/metaPixel.ts

import { retryUntilReady } from "./pixelRetry";

declare global {
	interface Window {
		fbq?: (...args: any[]) => void;
		_fbq?: any;
	}
}

/**
 * Initialize Meta (Facebook) Pixel only in production
 */
export const initMetaPixel = (): void => {
	const pixelId = process.env.REACT_APP_META_PIXEL_ID;

	if (!pixelId) {
		console.log("Meta Pixel disabled (no ID)");
		return;
	}

	// Inject the Meta Pixel script dynamically
	(function (f: any, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
		if (f.fbq) return;
		n = f.fbq = function (...args: any[]) {
			n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
		};
		if (!f._fbq) f._fbq = n;
		n.push = n;
		n.loaded = true;
		n.version = "2.0";
		n.queue = [];
		t = b.createElement(e);
		t.async = true;
		t.src = v;
		s = b.getElementsByTagName(e)[0];
		if (s?.parentNode) {
			s.parentNode.insertBefore(t, s);
		} else {
			b.head.appendChild(t);
		}
	})(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

	// Initialize Pixel and track initial page view
	window.fbq?.("init", pixelId);
	window.fbq?.("track", "PageView");

	// Add <noscript> fallback image for non-JS users
	const noscriptImg = document.createElement("img");
	noscriptImg.height = 1;
	noscriptImg.width = 1;
	noscriptImg.style.display = "none";
	noscriptImg.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
	document.body.appendChild(noscriptImg);

	console.log(`✅ Meta Pixel initialized: ${pixelId}`);
};

/**
 * Track a Meta Pixel event (standard or custom)
 */
export const trackMetaEvent = (eventName: string, params?: Record<string, any>, custom?: boolean): void => {
	const pixelId = process.env.REACT_APP_META_PIXEL_ID;

	if (!pixelId) {
		console.log(`Meta Pixel event skipped: ${eventName} (missing ID)`);
		return;
	}

	const fire = () => {
		if (!!custom) {
			window.fbq!("trackCustom", eventName, params);
		} else {
			window.fbq!("track", eventName, params);
		}
		console.log(`📊 Meta Pixel event: ${eventName}`, params);
	};

	if (typeof window.fbq === "function") {
		fire();
	} else {
		retryUntilReady(
			() => typeof window.fbq === "function",
			fire,
			() => console.warn(`⚠️ Meta Pixel not initialized, dropped event: ${eventName}`)
		);
	}
};
